import { FC, useMemo } from 'react';
import { SchedulingOutcomeAggregate } from '@weave/schema-gen-ts/dist/schemas/call-intelligence/v2';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { generateTrackingId, useCallIntelDefaultsShallowStore } from '@frontend/call-intel-core';
import { Trans, useTranslation } from '@frontend/i18n';
import { Text } from '@frontend/design-system';
import { useCallIntelDemoFlags } from '../../../hooks/use-call-intel-demo-flags';
import { useNavigateToCallRecordsWithFilter } from '../../../hooks/use-navigate-to-call-records-with-filter';
import { SchedulingStatsCard } from '../scheduling-stats-card';
import { StatItem } from '../scheduling-stats-card/stats-list';

const COMPONENT_TRACKING_ID = 'active-opportunities-card';

export const ActiveOpportunitiesCard: FC<{ schedulingAggregateStats?: SchedulingOutcomeAggregate }> = ({
  schedulingAggregateStats,
}) => {
  const { t } = useTranslation('call-intelligence');
  const { isDemoAccount } = useCallIntelDemoFlags();
  const { dataLabels } = useCallIntelDefaultsShallowStore('dataLabels');

  const { navigateToCallRecords } = useNavigateToCallRecordsWithFilter();

  const { scheduledPostCallCount, stillUnscheduledCount, unscheduledOnCall } = useMemo(() => {
    const scheduledOpportunitiesCount = schedulingAggregateStats?.scheduledOpportunitiesCount ?? 0;
    const scheduledOnCallCount = schedulingAggregateStats?.scheduledOnCallCount ?? 0;
    const scheduledPostCallCount = schedulingAggregateStats?.scheduledPostCallCount ?? 0;
    const stillUnscheduledCount = schedulingAggregateStats?.stillUnscheduledCount ?? 0;
    return {
      stillUnscheduledCount,
      scheduledPostCallCount,
      scheduledOnCallCount,
      scheduledOpportunitiesCount,
      unscheduledOnCall: scheduledOpportunitiesCount - scheduledOnCallCount,
    };
  }, [schedulingAggregateStats]);

  const statsListItems = useMemo<StatItem[]>(() => {
    return [
      {
        label:
          dataLabels.schedulingOutcomeV2Alternate?.[
            CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED
          ] ?? '',
        value: stillUnscheduledCount,
        trackingId: generateTrackingId({
          component: COMPONENT_TRACKING_ID,
          context: 'still-unscheduled',
          isDemoAccount,
        }),
        tooltip: t(
          'Calls that are still marked as unscheduled because no appointments were created within 7 days after the call'
        ),
        onClick: () =>
          navigateToCallRecords({
            schedulingOutcomeV2s: [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED],
          }),
        dataTestId: 'still-unscheduled-stat',
      },
      {
        label:
          dataLabels.schedulingOutcomeV2Alternate?.[
            CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL
          ] ?? '',
        value: scheduledPostCallCount,
        trackingId: generateTrackingId({
          component: COMPONENT_TRACKING_ID,
          context: 'recovered-opportunities',
          isDemoAccount,
        }),
        tooltip: t('Calls that were unscheduled, then had appointments created within 7 days after the call'),
        onClick: () =>
          navigateToCallRecords({
            schedulingOutcomeV2s: [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL],
          }),
        dataTestId: 'recovered-opportunities-stat',
      },
    ];
  }, [
    dataLabels.schedulingOutcomeV2Alternate,
    isDemoAccount,
    navigateToCallRecords,
    scheduledPostCallCount,
    stillUnscheduledCount,
    t,
  ]);

  return (
    <SchedulingStatsCard dataTestId='active-opportunities-card'>
      <SchedulingStatsCard.Header
        title={t('Active Opportunities')}
        subtitle={
          <Text size='large' as='p' data-testid='active-opportunities-subtitle'>
            <Trans t={t} count={unscheduledOnCall} values={{ unscheduledOnCall }}>
              Out of <strong>{{ unscheduledOnCall }}</strong> Unscheduled Calls
            </Trans>
          </Text>
        }
        callIntelTooltipId='activeOpportunities'
      />
      <SchedulingStatsCard.List items={statsListItems} />
      <SchedulingStatsCard.Footer
        text={t('Calls remain unscheduled if no appointments were scheduled within 7 days after.')}
      />
    </SchedulingStatsCard>
  );
};
